import {
  Combobox,
  ComboboxOption,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'corso-admin-ui';
import { DbJsonSchema } from 'corso-types';
import { ComponentProps, PropsWithChildren, useMemo } from 'react';
import { useThemeSettings } from '~/hooks/useThemeSettings';
import {
  useChangeStoreUser,
  useStoreUser,
} from '~/providers/StoreUserProvider';
import { useUser } from '~/providers/UserProvider';

/** The children are provided as additional content in the popover after the store selection. */
export default function StoreSwitcher({
  children,
  align,
}: PropsWithChildren<{
  align?: ComponentProps<typeof PopoverContent>['align'];
}>) {
  const user = useUser();
  const storeUser = useStoreUser();
  const changeStoreUser = useChangeStoreUser();
  const { data: themeSettings } = useThemeSettings();

  const storeOptions = useMemo(() => {
    const { storeUserRoles } = user;
    const storeNameCount = new Map<string, number>();
    storeUserRoles.forEach((storeUserRole) => {
      const count = storeNameCount.get(storeUserRole.store.name) ?? 0;
      storeNameCount.set(storeUserRole.store.name, count + 1);
    });

    return storeUserRoles
      .map((storeUserRole) => {
        const isDuplicateName =
          (storeNameCount.get(storeUserRole.store.name) ?? 0) > 1;

        const text = isDuplicateName
          ? `${storeUserRole.store.name} (ID: ${storeUserRole.store.id})`
          : storeUserRole.store.name;
        return {
          value: `${storeUserRole.store.id}`,
          keywords: [text],
          label: text,
        } satisfies ComboboxOption;
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [user]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 rounded-md bg-neutral-900 p-1 focus-within:bg-neutral-800 hover:bg-neutral-800 data-[state=open]:bg-neutral-800"
          disabled={storeOptions.length <= 1 && !children}
        >
          <div className="flex size-7 items-center justify-center rounded-lg bg-neutral-700 text-[0.625rem] font-medium text-white no-underline">
            {themeSettings?.merchantFaviconUrl &&
            themeSettings.merchantFaviconUrl !==
              DbJsonSchema.StoreUiConfig.defaultStoreUiTheme
                .merchantFaviconUrl ? (
              <img
                src={themeSettings.merchantFaviconUrl}
                alt={storeUser.store.name}
                // ? maybe apply grayscale filter, but should test with transparent favicons and favicons with backgrounds
                className="size-7 rounded-lg bg-cover bg-center p-1"
              />
            ) : (
              <Icon icon="store" />
            )}
          </div>
          <span
            className="hidden max-w-[24ch] truncate text-xs font-medium text-neutral-200 lg:block"
            title={storeUser.store.name}
          >
            {storeUser.store.name}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="flex flex-col gap-2 p-2" align={align}>
        {storeOptions.length > 1 && (
          <Combobox
            label="Select Store"
            options={storeOptions}
            placeholder="Search for a Store"
            value={`${storeUser.store.id}`}
            onChange={(storeId) =>
              storeId !== undefined &&
              changeStoreUser(Number.parseInt(storeId, 10))
            }
          />
        )}
        {storeOptions.length === 1 && (
          <p className="px-2 text-sm">{storeUser.store.name}</p>
        )}
        {children}
      </PopoverContent>
    </Popover>
  );
}
